import dayjs from "dayjs";
import type { Shift } from "../types/Shift";
import { recentEndedShifts } from "./rollover";

export interface UpcomingShiftsDayGroup {
	/** "Today" | "Tomorrow" | "Mon, Mar 5" — the display label for the day bucket. */
	label: string;
	/** Local-time start-of-day for the bucket; stable React key. */
	dayStartMs: number;
	shifts: Shift[];
}

function startOfDayMs(d: Date | dayjs.Dayjs): number {
	return dayjs(d).startOf("day").valueOf();
}

/**
 * Return shifts whose `endDate >= startOfDay(now)`, sorted by `startDate` ascending.
 * Shifts without an `endDate` are skipped (can't tell whether they're upcoming).
 * Ties on `startDate` are stable in insertion order.
 */
export function upcomingShifts(shifts: Shift[], now: Date): Shift[] {
	const cutoff = startOfDayMs(now);
	const withEnd: Array<{ shift: Shift; idx: number; start: number }> = [];
	for (let i = 0; i < shifts.length; i++) {
		const s = shifts[i];
		const end = s.endDate?.getTime();
		if (end === undefined) continue;
		if (end < cutoff) continue;
		withEnd.push({ shift: s, idx: i, start: s.startDate?.getTime() ?? 0 });
	}
	withEnd.sort((a, b) => a.start - b.start || a.idx - b.idx);
	return withEnd.map((w) => w.shift);
}

function labelForDay(dayStart: dayjs.Dayjs, today: dayjs.Dayjs): string {
	const tomorrow = today.add(1, "day");
	if (dayStart.isSame(today, "day")) return "Today";
	if (dayStart.isSame(tomorrow, "day")) return "Tomorrow";
	const sameYear = dayStart.isSame(today, "year");
	return dayStart.format(sameYear ? "ddd, MMM D" : "ddd, MMM D, YYYY");
}

/**
 * Group shifts by the local-day of their `startDate`. Preserves the order of the input array;
 * each group's `shifts` list keeps the input order. Shifts without a `startDate` fall into the
 * "Today" bucket (treated as "starting now") so they don't disappear.
 */
export function groupShiftsByDay(shifts: Shift[], now: Date): UpcomingShiftsDayGroup[] {
	const today = dayjs(now).startOf("day");
	const buckets = new Map<number, UpcomingShiftsDayGroup>();
	const order: number[] = [];
	for (const shift of shifts) {
		const start = shift.startDate ? dayjs(shift.startDate).startOf("day") : today;
		const key = start.valueOf();
		let bucket = buckets.get(key);
		if (!bucket) {
			bucket = { label: labelForDay(start, today), dayStartMs: key, shifts: [] };
			buckets.set(key, bucket);
			order.push(key);
		}
		bucket.shifts.push(shift);
	}
	return order.map((key) => buckets.get(key) as UpcomingShiftsDayGroup);
}

/** How the shift list buckets its cards: by start day (default) or by operator. */
export type ShiftGroupBy = "day" | "operator";

export const NO_OPERATOR_GROUP_LABEL = "(no operator)";

export interface UpcomingShiftsOperatorGroup {
	/** `operatorId`, or {@link NO_OPERATOR_GROUP_LABEL} for the operator-less bucket; stable React key. */
	key: string;
	/** Operator display name (or the no-operator label). */
	label: string;
	shifts: Shift[];
}

/**
 * Group shifts by `operatorId` (display name may collide across operators — MC keyed on the name
 * and merged them). Groups sort alphabetically by label; shifts without an operator bucket under
 * "(no operator)", pinned last (MC dropped them from the list entirely). Within a group the input
 * order is preserved — callers pass the `upcomingShifts` output, which is already startDate-sorted.
 */
export function groupShiftsByOperator(shifts: Shift[]): UpcomingShiftsOperatorGroup[] {
	const buckets = new Map<string, UpcomingShiftsOperatorGroup>();
	for (const shift of shifts) {
		const key = shift.operatorId || NO_OPERATOR_GROUP_LABEL;
		let bucket = buckets.get(key);
		if (!bucket) {
			bucket = { key, label: shift.operatorId ? shift.operator || key : NO_OPERATOR_GROUP_LABEL, shifts: [] };
			buckets.set(key, bucket);
		}
		bucket.shifts.push(shift);
	}
	const groups = Array.from(buckets.values());
	return groups.sort((a, b) => {
		if (a.key === NO_OPERATOR_GROUP_LABEL) return 1;
		if (b.key === NO_OPERATOR_GROUP_LABEL) return -1;
		return a.label.localeCompare(b.label);
	});
}

/**
 * The scheduling list's shift universe — MC parity (recent-parity plan): MC has no
 * recent/past section; a past shift stays in its one date-grouped list exactly while a
 * still-relevant (unsampled) job references it, fully editable, and disappears once the
 * work is done. Here: recent-ended shifts (the 7-day window stands in for MC's Airtable
 * view filter) with at least one loaded-and-unsampled job, ascending, then the upcoming
 * shifts. A past shift whose jobs are not loaded yet stays hidden — it pops in when the
 * prefetch resolves rather than flickering out.
 */
export function schedulingListShifts(
	shifts: Shift[],
	now: Date,
	unsampledByJobId: ReadonlyMap<string, boolean>,
): Shift[] {
	const pastWithWork = recentEndedShifts(shifts, now)
		.filter((s) => s.jobIds.some((id) => unsampledByJobId.get(id) === true))
		.reverse(); // recentEndedShifts sorts most-recent-first; the list reads ascending
	return [...pastWithWork, ...upcomingShifts(shifts, now)];
}
